import { Pause, Play, SkipForward, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { loadRadioState, radioHeartbeat, saveRadioState, TRACKS } from "./RadioBoombox";
import { toast } from "./ui/sonner";

export function FloatingRadio() {
  const audioRef = useRef(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("dolo-radio-chip-hidden") === "1");
  const dismissedRef = useRef(dismissed);
  const restoredRef = useRef(loadRadioState());
  const [index, setIndex] = useState(() => restoredRef.current?.index ?? 0);
  const [playing, setPlaying] = useState(false);
  const indexRef = useRef(index);
  const pendingSeekRef = useRef(restoredRef.current?.time ?? 0);
  const lastSaveRef = useRef(0);
  const unlockPromiseRef = useRef(null);

  // the chip's own deck — loops the same station rotation, resumes mid-broadcast
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "none";
    audio.volume = 0.7;
    audioRef.current = audio;
    const onEnded = () => setIndex((i) => (i + 1) % TRACKS.length);
    const onTime = () => {
      if (audio.currentTime - lastSaveRef.current >= 5) {
        lastSaveRef.current = audio.currentTime;
        saveRadioState(indexRef.current, audio.currentTime);
      }
    };
    const onMeta = () => {
      if (pendingSeekRef.current > 0 && Number.isFinite(audio.duration)) {
        audio.currentTime = Math.min(pendingSeekRef.current, Math.max(0, audio.duration - 1));
      }
      pendingSeekRef.current = 0;
    };
    const onHide = () => saveRadioState(indexRef.current, audio.currentTime);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    window.addEventListener("pagehide", onHide);
    const onOther = (event) => {
      if (event.detail === "floating") return;
      if (!audio.paused) {
        saveRadioState(indexRef.current, audio.currentTime);
        audio.pause();
        setPlaying(false);
      }
    };
    // fired synchronously inside the gate's submit click so mobile browsers
    // mark this element as gesture-unlocked before access is even granted.
    // We intentionally do NOT pause here — the autostart handoff unmutes and
    // keeps the same element playing. Pausing raced the autostart and the
    // station silently never started (the unlock's deferred pause landed
    // AFTER the autostart check and killed the broadcast on every device).
    const onUnlock = () => {
      if (!audio.paused && !audio.muted) return;
      if (!audio.src) audio.src = TRACKS[indexRef.current].src;
      audio.muted = true;
      unlockPromiseRef.current = audio.play().catch(() => {});
    };
    // brief "NOW PLAYING" flash so visitors notice the station started on its own
    const announceNowPlaying = () => {
      const track = TRACKS[indexRef.current];
      toast(`NOW PLAYING — ${track.title}`, {
        description: track.artist,
        duration: 4500,
        className: "radio-now-playing",
      });
    };
    // shared audible-start helper — used by the gate autostart, the
    // returning-session attempt, and the first-gesture fallback (browsers block
    // sound on reloads until the visitor taps/presses something, so the first
    // gesture anywhere starts the station: reload continue, account login
    // click, first tap on a page)
    const tryAutoStart = () => {
      if (dismissedRef.current) return;
      if (!audio.paused) return;
      if (!audio.src) audio.src = TRACKS[indexRef.current].src;
      audio.muted = false;
      window.dispatchEvent(new CustomEvent("dolo-radio-playing", { detail: "floating" }));
      audio.play().then(() => { setPlaying(true); announceNowPlaying(); }).catch(() => {});
    };
    // one-shot first-gesture fallback, armed whenever an audible start was
    // blocked (strict autoplay policies, iOS unmute rejection, reloads)
    let offFirstGesture = null;
    const armFirstGesture = () => {
      if (offFirstGesture) return;
      const onFirstGesture = () => {
        if (sessionStorage.getItem("dolo-gate-access") !== "granted") return; // stay armed until the gate lifts
        offFirstGesture();
        offFirstGesture = null;
        tryAutoStart();
      };
      offFirstGesture = () => {
        window.removeEventListener("pointerdown", onFirstGesture);
        window.removeEventListener("touchstart", onFirstGesture);
        window.removeEventListener("keydown", onFirstGesture);
      };
      window.addEventListener("pointerdown", onFirstGesture);
      window.addEventListener("touchstart", onFirstGesture, { passive: true });
      window.addEventListener("keydown", onFirstGesture);
    };
    // fired the moment the gate grants access — wait out the muted unlock
    // play, then unmute and continue on the same element; if a strict browser
    // still refuses the unmuted start, the next tap anywhere kicks it off
    const onAutostart = () => {
      if (dismissedRef.current) return;
      const startPlayback = () => {
        if (!audio.src) audio.src = TRACKS[indexRef.current].src;
        audio.muted = false;
        window.dispatchEvent(new CustomEvent("dolo-radio-playing", { detail: "floating" }));
        audio.play().then(() => { setPlaying(true); announceNowPlaying(); }).catch(() => armFirstGesture());
      };
      const pending = unlockPromiseRef.current;
      if (pending) pending.finally(startPlayback);
      else startPlayback();
    };
    window.addEventListener("dolo-radio-playing", onOther);
    window.addEventListener("dolo-radio-unlock", onUnlock);
    window.addEventListener("dolo-radio-autostart", onAutostart);
    // returning session (gate already granted this visit): try to resume the
    // station — browsers with media engagement allow it; where sound is blocked
    // until a gesture, the first tap/click/keypress anywhere starts it instead
    if (sessionStorage.getItem("dolo-gate-access") === "granted" && !dismissedRef.current) {
      tryAutoStart();
      armFirstGesture();
    }
    return () => {
      audio.pause();
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("dolo-radio-playing", onOther);
      window.removeEventListener("dolo-radio-unlock", onUnlock);
      window.removeEventListener("dolo-radio-autostart", onAutostart);
      if (offFirstGesture) offFirstGesture();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // presence: this chip counts as a tuned-in runner while it plays
  useEffect(() => {
    if (!playing) return undefined;
    radioHeartbeat();
    const timer = setInterval(radioHeartbeat, 30000);
    return () => clearInterval(timer);
  }, [playing]);

  // track change — paused deck holds off downloading until the visitor presses play
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    indexRef.current = index;
    lastSaveRef.current = 0;
    saveRadioState(index, 0);
    if (playing) {
      if (!audio.src || !audio.src.endsWith(TRACKS[index].src)) audio.src = TRACKS[index].src;
      audio.play().catch(() => setPlaying(false));
    } else if (audio.src && !audio.src.endsWith(TRACKS[index].src)) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  if (dismissed) return null;

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      saveRadioState(index, audio.currentTime);
      audio.pause();
      setPlaying(false);
    } else {
      if (!audio.src) audio.src = TRACKS[index].src;
      window.dispatchEvent(new CustomEvent("dolo-radio-playing", { detail: "floating" }));
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const next = () => setIndex((i) => (i + 1) % TRACKS.length);

  const dismiss = () => {
    if (audioRef.current) {
      saveRadioState(indexRef.current, audioRef.current.currentTime);
      audioRef.current.pause();
    }
    dismissedRef.current = true;
    sessionStorage.setItem("dolo-radio-chip-hidden", "1");
    setDismissed(true);
  };

  const track = TRACKS[index];
  const label = `${track.title} // ${track.artist}`;

  return (
    <div className={`floating-radio ${playing ? "is-playing" : ""}`} data-testid="floating-radio">
      <span className="floating-radio-pulse" aria-hidden="true" />
      <button
        type="button"
        className="floating-radio-toggle"
        onClick={toggle}
        data-testid="floating-radio-toggle"
        aria-label={playing ? "Turn radio off" : "Turn radio on"}
      >
        {playing ? <Pause size={13} /> : <Play size={13} />}
      </button>
      <button type="button" onClick={next} data-testid="floating-radio-next" aria-label="Next track">
        <SkipForward size={12} />
      </button>
      <Link to="/wasteland-radio" className="floating-radio-title" data-testid="floating-radio-title" title="Open Wasteland Radio">
        <span className="fr-marquee">
          <b>{label}</b>
          <b aria-hidden="true">{label}</b>
        </span>
      </Link>
      <button
        type="button"
        className="floating-radio-close"
        onClick={dismiss}
        data-testid="floating-radio-close"
        aria-label="Remove radio from page"
      >
        <X size={13} />
      </button>
    </div>
  );
}
